import React, { useState, useEffect } from 'react';
import { Users, Plus, Edit, Trash2, Save, X, Sparkles, MoveUp, MoveDown, CheckCircle2, AlertCircle } from 'lucide-react';
import { StorageService } from '../../services/storageService';

interface FacultyMember {
  _id?: string;
  name: string;
  title: string;
  credentials: string;
  bio: string;
  avatarUrl?: string;
  specialties: string[];
  order: number;
  isDesignatedAuthor: boolean;
  email?: string;
}

export const FacultyManager: React.FC = () => {
  const [faculty, setFaculty] = useState<FacultyMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<FacultyMember | null>(null);
  
  // Form fields
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [credentials, setCredentials] = useState('RN');
  const [bio, setBio] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [specialtiesText, setSpecialtiesText] = useState('');
  const [order, setOrder] = useState(0);
  const [isDesignatedAuthor, setIsDesignatedAuthor] = useState(true);
  const [email, setEmail] = useState('');

  // UI state
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const loadFaculty = async () => {
    setIsLoading(true);
    try {
      const data = await StorageService.getFaculty();
      setFaculty(data);
    } catch (err) {
      console.error("Failed to load faculty:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadFaculty();
  }, []);

  const openAddForm = () => {
    setEditingMember(null);
    setName('');
    setTitle('');
    setCredentials('RN');
    setBio('');
    setAvatarUrl('');
    setSpecialtiesText('');
    setOrder(faculty.length + 1);
    setIsDesignatedAuthor(true);
    setEmail('');
    setIsFormOpen(true);
    setErrorMsg('');
  };

  const openEditForm = (member: FacultyMember) => {
    setEditingMember(member);
    setName(member.name);
    setTitle(member.title);
    setCredentials(member.credentials);
    setBio(member.bio);
    setAvatarUrl(member.avatarUrl || '');
    setSpecialtiesText(member.specialties ? member.specialties.join(', ') : '');
    setOrder(member.order);
    setIsDesignatedAuthor(member.isDesignatedAuthor);
    setEmail(member.email || '');
    setIsFormOpen(true);
    setErrorMsg('');
  };

  const triggerToast = (msg: string, type: 'success' | 'error') => {
    if (type === 'success') {
      setSuccessMsg(msg);
      setTimeout(() => setSuccessMsg(''), 3000);
    } else {
      setErrorMsg(msg);
      setTimeout(() => setErrorMsg(''), 4000);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !title || !bio) {
      setErrorMsg('Please specify Name, Professional Title, and Biography details.');
      return;
    }

    const payload: FacultyMember = {
      ...(editingMember?._id ? { _id: editingMember._id } : {}),
      name,
      title,
      credentials,
      bio,
      avatarUrl,
      specialties: specialtiesText ? specialtiesText.split(',').map(s => s.trim()).filter(Boolean) : [],
      order: Number(order) || 0,
      isDesignatedAuthor,
      email
    };

    try {
      await StorageService.saveFaculty(payload);
      triggerToast(editingMember ? "Faculty details updated" : "Faculty profile added successfully", "success");
      setIsFormOpen(false);
      loadFaculty();
    } catch (err: any) {
      triggerToast(err.message || "Failed to commit faculty profile", "error");
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this faculty member? This is irreversible.")) return;
    try {
      await StorageService.deleteFaculty(id);
      triggerToast("Faculty profile deleted", "success");
      loadFaculty();
    } catch (err: any) {
      triggerToast(err.message || "Failed to delete profile", "error");
    }
  };

  const handleMove = async (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index === 0) return;
    if (direction === 'down' && index === faculty.length - 1) return;

    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    const listCopy = [...faculty];
    
    // Swap order values
    const tempOrder = listCopy[index].order;
    listCopy[index].order = listCopy[targetIndex].order;
    listCopy[targetIndex].order = tempOrder;

    try {
      await Promise.all([
        StorageService.saveFaculty(listCopy[index]),
        StorageService.saveFaculty(listCopy[targetIndex])
      ]);
      loadFaculty();
    } catch (err: any) {
      triggerToast("Failed to shift list ordering: " + err.message, "error");
    }
  };

  return (
    <div className="space-y-6 md:space-y-8 animate-fade-in pb-16 px-2 md:px-0">
      
      {/* Header and Add button */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl md:text-3.5xl font-black text-slate-900 dark:text-white tracking-tight">
            Faculty Directory Management
          </h2>
          <p className="text-xs md:text-sm text-slate-505 dark:text-slate-400 font-medium">
            Manage the list of licensed nursing instructors and authors who draft our study guides and questions.
          </p>
        </div>
        <button 
          onClick={openAddForm}
          className="px-6 py-3.5 bg-rose-600 text-white rounded-2xl font-black text-xs tracking-widest uppercase hover:bg-rose-700 transition-all flex items-center gap-2 shadow-lg shadow-rose-600/15"
        >
          <Plus size={16} /> Add Educator Profile
        </button>
      </div>

      {/* Success/Error Alerts */}
      {successMsg && (
        <div className="bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-900 px-6 py-4 rounded-2xl flex items-center gap-3">
          <CheckCircle2 size={18} />
          <span className="text-xs font-black uppercase tracking-wider">{successMsg}</span>
        </div>
      )}
      {errorMsg && (
        <div className="bg-rose-50 dark:bg-rose-950/30 text-rose-500 dark:text-rose-405 border border-rose-100 dark:border-rose-900 px-6 py-4 rounded-2xl flex items-center gap-3">
          <AlertCircle size={18} />
          <span className="text-xs font-black uppercase tracking-wider">{errorMsg}</span>
        </div>
      )}

      {/* Main List & Form Modal */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        
        {/* Faculty List table */}
        <div className={`lg:col-span-12 ${isFormOpen ? 'lg:col-span-7' : 'lg:col-span-12'} space-y-4`}>
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[2.5rem] shadow-sm overflow-hidden">
            <div className="p-6 md:p-8 border-b border-slate-100 dark:border-slate-805 bg-slate-50/50 dark:bg-slate-950/30 flex justify-between items-center">
              <h3 className="text-sm font-black text-slate-850 dark:text-slate-205 flex items-center gap-2">
                <Users size={18} /> Current Faculty Members ({faculty.length})
              </h3>
            </div>

            {isLoading ? (
              <div className="p-12 text-center text-slate-400 font-bold uppercase text-[10px] tracking-widest">
                Fetching Profiles...
              </div>
            ) : faculty.length === 0 ? (
              <div className="p-12 text-center text-slate-400 font-bold">
                No profiles loaded. Add profiles or trigger the GET request to seed defaults.
              </div>
            ) : (
              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {faculty.map((member, index) => (
                  <div key={member._id} className="p-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between hover:bg-slate-50/50 dark:hover:bg-slate-950/20 transition-all">
                    <div className="flex gap-4 items-center min-w-0">
                      <div className="shrink-0">
                        {member.avatarUrl ? (
                          <img src={member.avatarUrl} alt={member.name} className="w-12 h-12 rounded-xl object-cover border border-slate-200 dark:border-slate-800" />
                        ) : (
                          <div className="w-12 h-12 bg-slate-100 dark:bg-slate-800 text-slate-500 rounded-xl flex items-center justify-center font-black">
                            {member.name.charAt(0)}
                          </div>
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-extrabold text-sm text-slate-900 dark:text-white truncate">{member.name}</h4>
                          {member.isDesignatedAuthor && (
                            <span className="px-1.5 py-0.5 bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400 rounded text-[7px] font-black uppercase tracking-widest border border-indigo-100 dark:border-indigo-900">
                              Author
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] font-bold text-slate-505 dark:text-slate-400 truncate">{member.title}</p>
                        <p className="text-[9px] text-slate-400 font-bold tracking-widest mt-0.5 truncate">{member.credentials}</p>
                      </div>
                    </div>

                    {/* Actions and Sorting ordering buttons */}
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="flex items-center border border-slate-150 dark:border-slate-800 rounded-lg overflow-hidden mr-2">
                        <button
                          onClick={() => handleMove(index, 'up')}
                          disabled={index === 0}
                          className="p-2 text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:hover:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-950 transition-colors"
                          title="Move Up"
                        >
                          <MoveUp size={14} />
                        </button>
                        <div className="w-px h-4 bg-slate-150 dark:bg-slate-800"></div>
                        <button
                          onClick={() => handleMove(index, 'down')}
                          disabled={index === faculty.length - 1}
                          className="p-2 text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:hover:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-950 transition-colors"
                          title="Move Down"
                        >
                          <MoveDown size={14} />
                        </button>
                      </div>

                      <button
                        onClick={() => openEditForm(member)}
                        className="p-2 text-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-950/40 rounded-lg transition-all"
                        title="Edit Details"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(member._id!)}
                        className="p-2 text-rose-500 hover:bg-rose-55 hover:text-rose-600 rounded-lg transition-all"
                        title="Delete Profile"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Modal Form Overlay / Side column */}
        {isFormOpen && (
          <div className="lg:col-span-5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[2.5rem] shadow-xl p-6 md:p-8 space-y-6 animate-pop">
            <div className="flex justify-between items-center border-b border-slate-100 dark:border-slate-805 pb-4">
              <h3 className="text-sm font-black text-slate-850 dark:text-slate-205 flex items-center gap-2">
                <Sparkles size={18} className="text-amber-500 fill-amber-400/10" /> {editingMember ? "Edit Faculty Profile" : "Add Faculty Profile"}
              </h3>
              <button 
                onClick={() => setIsFormOpen(false)}
                className="p-2 text-slate-400 hover:text-slate-700 rounded-lg"
              >
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Legal Name</label>
                <input 
                  type="text" 
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g. Gurpreet Singh"
                  required
                  className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-950 border border-slate-150 dark:border-slate-800 rounded-xl outline-none focus:border-rose-500 font-bold text-xs"
                />
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Official Title</label>
                <input 
                  type="text" 
                  value={title}
                  onChange={e => setTitle(e.target.value)}
                  placeholder="e.g. Program Director & Nurse Educator"
                  required
                  className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-950 border border-slate-150 dark:border-slate-800 rounded-xl outline-none focus:border-rose-500 font-bold text-xs"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Credentials String</label>
                  <input 
                    type="text" 
                    value={credentials}
                    onChange={e => setCredentials(e.target.value)}
                    placeholder="e.g. MSN, RN"
                    className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-950 border border-slate-150 dark:border-slate-800 rounded-xl outline-none focus:border-rose-500 font-bold text-xs"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Sorting Order Weight</label>
                  <input 
                    type="number" 
                    value={order}
                    onChange={e => setOrder(Number(e.target.value))}
                    placeholder="e.g. 1"
                    className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-950 border border-slate-150 dark:border-slate-800 rounded-xl outline-none focus:border-rose-500 font-bold text-xs"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Specialties (Comma Separated)</label>
                <input 
                  type="text" 
                  value={specialtiesText}
                  onChange={e => setSpecialtiesText(e.target.value)}
                  placeholder="e.g. Infection Control, Bedside Etiquette"
                  className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-950 border border-slate-150 dark:border-slate-800 rounded-xl outline-none focus:border-rose-500 font-bold text-xs"
                />
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Profile Photo Link (Avatar URL)</label>
                <input 
                  type="text" 
                  value={avatarUrl}
                  onChange={e => setAvatarUrl(e.target.value)}
                  placeholder="e.g. Unsplash or direct image URL"
                  className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-950 border border-slate-150 dark:border-slate-800 rounded-xl outline-none focus:border-rose-500 font-bold text-xs"
                />
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Contact Email Address</label>
                <input 
                  type="email" 
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="e.g. contact@diamondnursingschool.com"
                  className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-950 border border-slate-150 dark:border-slate-800 rounded-xl outline-none focus:border-rose-500 font-bold text-xs"
                />
              </div>

              <div className="flex items-center gap-2 py-1">
                <input 
                  type="checkbox" 
                  id="author-checkbox"
                  checked={isDesignatedAuthor}
                  onChange={e => setIsDesignatedAuthor(e.target.checked)}
                  className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                />
                <label htmlFor="author-checkbox" className="text-[11px] font-extrabold text-slate-650 dark:text-slate-350 select-none">Designated Curriculum Content Author</label>
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Professional Biography</label>
                <textarea 
                  rows={4}
                  value={bio}
                  onChange={e => setBio(e.target.value)}
                  required
                  placeholder="Write a descriptive bio of their pedagogical credentials or nursing expert credentials..."
                  className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-950 border border-slate-150 dark:border-slate-800 rounded-xl outline-none focus:border-rose-500 font-bold text-xs resize-none"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button 
                  type="submit"
                  className="flex-1 py-3.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-black tracking-widest uppercase transition-all flex items-center justify-center gap-2"
                >
                  <Save size={16} /> Save Changes
                </button>
                <button 
                  type="button"
                  onClick={() => setIsFormOpen(false)}
                  className="px-5 py-3.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-xl text-xs font-black tracking-widest uppercase hover:bg-slate-200"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

      </div>
    </div>
  );
};
